import { HebrewCalendar } from "@hebcal/core";

/**
 * U.S. holiday presets for celebrations (AddGifteeWizard + giftee profile).
 * The order is the product's calendar order; IDs map to Firestore reminder
 * `type` values and must remain stable for existing reminders.
 */
const US_HOLIDAY_PRESETS = [
  { id: "new-years", name: "New Year's Day", getDate: (year) => new Date(year, 0, 1) },
  { id: "valentines", name: "Valentine's Day", getDate: (year) => new Date(year, 1, 14) },
  { id: "st-patricks-day", name: "St. Patrick's Day", getDate: (year) => new Date(year, 2, 17) },
  { id: "easter", name: "Easter", getDate: calculateEaster },
  {
    id: "mothers-day",
    name: "Mother's Day",
    getDate: (year) => getNthWeekdayOfMonth(year, 4, 0, 2),
  },
  {
    id: "fathers-day",
    name: "Father's Day",
    getDate: (year) => getNthWeekdayOfMonth(year, 5, 0, 3),
  },
  { id: "halloween", name: "Halloween", getDate: (year) => new Date(year, 9, 31) },
  {
    id: "thanksgiving",
    name: "Thanksgiving",
    getDate: (year) => getNthWeekdayOfMonth(year, 10, 4, 4),
  },
  { id: "hanukkah", name: "Hanukkah", getDate: calculateHanukkah },
  { id: "christmas", name: "Christmas", getDate: (year) => new Date(year, 11, 25) },
];

export const US_HOLIDAY_PRESET_IDS = new Set(
  US_HOLIDAY_PRESETS.map((preset) => preset.id),
);

function getNthWeekdayOfMonth(year, month, weekday, occurrence) {
  const firstDay = new Date(year, month, 1);
  const daysToFirstWeekday = (weekday - firstDay.getDay() + 7) % 7;
  return new Date(year, month, 1 + daysToFirstWeekday + (occurrence - 1) * 7);
}

function calculateEaster(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
}

function calculateHanukkah(year) {
  const firstCandle = HebrewCalendar.calendar({
    year,
    isHebrewYear: false,
    il: false,
  }).find((event) => event.getDesc() === "Chanukah: 1 Candle");
  if (!firstCandle) throw new Error(`Could not calculate Hanukkah for ${year}`);
  return firstCandle.getDate().greg();
}

function toMmDd(date) {
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

export function getUsHolidayPreset(id) {
  return US_HOLIDAY_PRESETS.find((preset) => preset.id === id) ?? null;
}

export function getUsHolidayDate(id, year = new Date().getFullYear()) {
  const preset = getUsHolidayPreset(id);
  return preset ? preset.getDate(year) : null;
}

/** Returns the product-ordered preset list with dates calculated for `year`. */
export function getUsHolidayOccasions(year = new Date().getFullYear()) {
  return US_HOLIDAY_PRESETS.map((preset) => {
    const dateObject = preset.getDate(year);
    return { id: preset.id, name: preset.name, date: toMmDd(dateObject), dateObject };
  });
}

/** Resolves the next occurrence, including a moving holiday's correct next-year date. */
export function getNextUsHolidayOccurrence(id, now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisYear = getUsHolidayDate(id, today.getFullYear());
  if (!thisYear) return null;
  return thisYear < today
    ? getUsHolidayDate(id, today.getFullYear() + 1)
    : thisYear;
}

/**
 * Replaces stale stored dates/titles for known preset reminders at the read
 * boundary. Firestore data remains untouched; custom and user-entered dates
 * retain their original values.
 */
export function getEffectiveReminder(reminder, now = new Date()) {
  const preset = getUsHolidayPreset(reminder?.type);
  if (!preset) return reminder;
  return {
    ...reminder,
    title: preset.name,
    date: getNextUsHolidayOccurrence(preset.id, now),
  };
}
